import React, { useCallback, useEffect, useState } from 'react';
import { faArrowDownArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { values } from 'lodash';
import { useFormContext } from 'react-hook-form';
import {
  AddressStateOrProvinceEnum,
  FieldObject,
} from '../../../openapi/atlantis';
import { capitalizeEnum } from '../../../utils/EnumHelper';
import { getGoogleAutoCompleteInputValidations } from '../../../utils/Validations';
import Button from '../../Button';
import ControlledGoogleAutoCompleteSearchInput from '../../inputs/ControlledGoogleAutoCompleteSearchInput';
import ControlledSingleSelectDropDownInput from '../../inputs/ControlledSingleSelectDropDownInput';
import ControlledTextInput from '../../inputs/ControlledTextInput';
import { GooglePlaceAddressType } from '../../../utils/GoogleMapUtils';

interface AddressInputProps {
  name: string;
  field: FieldObject;
}

const VALIDATION_TO_FIELD_MAP: Record<string, string> = {
  STREET_ADDRESS1: 'streetAddress1',
  STREET_ADDRESS2: 'streetAddress2',
  CITY: 'city',
  STATE_OR_PROVINCE: 'stateOrProvince',
  ZIP_OR_POSTAL_CODE: 'zipOrPostalCode',
  COUNTY: 'county',
  COUNTRY: 'country',
};

export const getRequiredAddressFields = (
  fieldWithValidations: FieldObject,
): Set<string> => {
  const requiredFields = new Set<string>();
  const validations = fieldWithValidations?.validations || [];

  validations.forEach((validation: any) => {
    if (validation.type === 'addressComponent' && validation.values) {
      validation.values.forEach((value: string) => {
        const fieldName = VALIDATION_TO_FIELD_MAP[value];
        if (fieldName) {
          requiredFields.add(fieldName);
        }
      });
    }
  });

  return requiredFields;
};

const AddressInput: React.FC<AddressInputProps> = ({ name, field }) => {
  const { control, setValue, setError } = useFormContext();
  const [isManualAddress, setIsManualAddress] = useState<boolean>(false);

  useEffect(() => {
    setValue(`${name}.isManualAddress`, isManualAddress);
  }, [isManualAddress, name, setValue]);

  const handleAddressError = useCallback(
    async (error: GooglePlaceAddressType | undefined) => {
      if (!error) {
        return;
      }

      const getPath = (field: string) => `${name}.${field}`;

      setIsManualAddress(true);

      (Object.keys(error) as Array<keyof GooglePlaceAddressType>).forEach(
        (key) => {
          const val = error?.[key];
          setValue(
            getPath(key),
            val ? (key === 'county' ? capitalizeEnum(val) : val) : '',
            { shouldValidate: false },
          );
        },
      );

      const requiredFields = Array.from(getRequiredAddressFields(field));

      setTimeout(() => {
        requiredFields.forEach((field) => {
          const fieldKey = field as keyof GooglePlaceAddressType;
          if (!error[fieldKey]) {
            setError(getPath(field), { type: 'manual', message: 'Required' });
          }
        });
      }, 100);
    },
    [name, setValue, setError, field],
  );

  const isAddressFieldRequired = (fieldName: string): string | undefined => {
    const requiredFields = getRequiredAddressFields(field);
    return requiredFields.has(fieldName) ? 'Required' : undefined;
  };

  return (
    <div>
      {isManualAddress ? (
        <div>
          <div>
            <ControlledTextInput
              name={`${name}.streetAddress1`}
              placeholder='Street'
              label='Street Address'
              control={control}
              autoComplete='address-line1'
              rules={{ required: isAddressFieldRequired('streetAddress1') }}
            />
          </div>
          <div className='mt-6'>
            <ControlledTextInput
              label='Street Address 2'
              name={`${name}.streetAddress2`}
              placeholder='Street Line 2'
              control={control}
              autoComplete='address-line2'
              rules={{ required: isAddressFieldRequired('streetAddress2') }}
            />
          </div>
          <div className='mt-6'>
            <ControlledTextInput
              name={`${name}.county`}
              label='County'
              placeholder='County'
              control={control}
              autoComplete='address-level3'
              rules={{
                required: isAddressFieldRequired('county'),
              }}
            />
          </div>
          <div className='mt-6'>
            <ControlledTextInput
              label='City'
              name={`${name}.city`}
              placeholder='City'
              control={control}
              autoComplete='address-level2'
              rules={{ required: isAddressFieldRequired('city') }}
            />
          </div>
          <div className='mt-6'>
            <ControlledSingleSelectDropDownInput
              name={`${name}.stateOrProvince`}
              label='State'
              placeholder='State'
              control={control}
              options={values(AddressStateOrProvinceEnum).map((c) => ({
                label: capitalizeEnum(c),
                value: c,
              }))}
              autoComplete='address-level1'
              rules={{ required: isAddressFieldRequired('stateOrProvince') }}
            />
          </div>
          <div className='mt-6'>
            <ControlledTextInput
              name={`${name}.zipOrPostalCode`}
              placeholder='Zip Code'
              control={control}
              label='Zip Code'
              autoComplete='postal-code'
              rules={{ required: isAddressFieldRequired('zipOrPostalCode') }}
            />
          </div>
        </div>
      ) : (
        <ControlledGoogleAutoCompleteSearchInput
          name={name}
          control={control}
          label={field.label!}
          //@ts-ignore
          placeholder={field.placeholder}
          rules={getGoogleAutoCompleteInputValidations(field)}
          onAddressError={handleAddressError}
          field={field}
        />
      )}
      <div className='flex flex-row my-3 justify-end'>
        <Button
          label={isManualAddress ? 'Search Address' : 'Enter address manually'}
          onPress={() => setIsManualAddress(!isManualAddress)}
          variant='no-border'
          buttonSize='sm'
          textSize='xs'
          textStyle='text-primary-skyblue'
          leftIcon={
            <FontAwesomeIcon
              icon={faArrowDownArrowUp}
              fontSize={12}
              className='text-primary-skyblue mb-0.5'
            />
          }
        />
      </div>
    </div>
  );
};

export default AddressInput;
